export interface GoalscorerEntry {
  team: "A" | "B";
  playerId: number;
}

export interface RatingMatch {
  teamA?: number[];
  teamB?: number[];
  teama?: number[];
  teamb?: number[];
  winner?: "A" | "B" | "Draw";
  goalscorers?: GoalscorerEntry[];
}

export interface PlayerRatings {
  skills: number;
  effort: number;
  stamina: number;
}

export interface RatingSuggestion {
  playerId: number;
  skills?: { from: number; to: number };
  effort?: { from: number; to: number };
  stamina?: { from: number; to: number };
  reasonParams: { games: number; wins: number; goals: number };
}

function teams(match: RatingMatch) {
  return {
    teamA: match.teamA ?? match.teama ?? [],
    teamB: match.teamB ?? match.teamb ?? [],
  };
}

function clampRating(value: number): number {
  return Math.max(1, Math.min(100, Math.round(value)));
}

export function suggestRatingAdjustments(
  playerId: number,
  matches: RatingMatch[],
  current: PlayerRatings,
  lastN = 5
): RatingSuggestion | null {
  const recentGroupMatches = matches.slice(0, lastN);
  const playerMatches = recentGroupMatches.filter((m) => {
    const { teamA, teamB } = teams(m);
    return teamA.includes(playerId) || teamB.includes(playerId);
  });

  if (playerMatches.length < 2) return null;

  let wins = 0;
  let goals = 0;

  for (const match of playerMatches) {
    const { teamA, teamB } = teams(match);
    const inA = teamA.includes(playerId);
    const won =
      (inA && match.winner === "A") || (!inA && match.winner === "B");
    if (won) wins++;

    for (const gs of match.goalscorers ?? []) {
      if (gs.playerId === playerId) goals++;
    }
  }

  const winRate = wins / playerMatches.length;
  const goalsPerGame = goals / playerMatches.length;

  let skillsDelta = 0;
  let effortDelta = 0;
  let staminaDelta = 0;

  if (winRate >= 0.7) {
    skillsDelta += 2;
    effortDelta += 1;
  } else if (winRate <= 0.3) {
    skillsDelta -= 2;
    effortDelta -= 1;
  }

  if (goalsPerGame >= 1) skillsDelta += 2;
  else if (goalsPerGame >= 0.5) skillsDelta += 1;

  if (playerMatches.length >= 4) staminaDelta += 1;

  const cap = (delta: number) => Math.max(-5, Math.min(5, delta));
  skillsDelta = cap(skillsDelta);
  effortDelta = cap(effortDelta);
  staminaDelta = cap(staminaDelta);

  if (skillsDelta === 0 && effortDelta === 0 && staminaDelta === 0) return null;

  const suggestion: RatingSuggestion = {
    playerId,
    reasonParams: {
      games: playerMatches.length,
      wins,
      goals,
    },
  };

  if (skillsDelta !== 0) {
    suggestion.skills = {
      from: current.skills,
      to: clampRating(current.skills + skillsDelta),
    };
  }
  if (effortDelta !== 0) {
    suggestion.effort = {
      from: current.effort,
      to: clampRating(current.effort + effortDelta),
    };
  }
  if (staminaDelta !== 0) {
    suggestion.stamina = {
      from: current.stamina,
      to: clampRating(current.stamina + staminaDelta),
    };
  }

  return suggestion;
}

export function getGroupAverageRatings(
  players: { skills?: number; effort?: number; stamina?: number }[]
): PlayerRatings {
  if (players.length === 0) return { skills: 75, effort: 75, stamina: 75 };
  const sum = players.reduce(
    (acc, p) => ({
      skills: acc.skills + (p.skills ?? 75),
      effort: acc.effort + (p.effort ?? 75),
      stamina: acc.stamina + (p.stamina ?? 75),
    }),
    { skills: 0, effort: 0, stamina: 0 }
  );
  const n = players.length;
  return {
    skills: Math.round(sum.skills / n),
    effort: Math.round(sum.effort / n),
    stamina: Math.round(sum.stamina / n),
  };
}
